import type { ColorLockedLegendItem } from "store/features/app/appSlice";

import { mBinSize } from "models/binSize";
import { AggregationType, IGroupBy } from "models/groupBy";

const MAX_WARNED_BINS = 100;
const warnedMissingBins = new Set<string>();

/** Exported for test cleanup only. */
export const _warnedMissingBins = warnedMissingBins;

function cleanBin(bin: mBinSize) {
  // NOTE: This sanitizer targets the C# map/chart-svc BuildGroupByRequest
  // handler, which has a different bound-semantics contract than the Rust
  // eva-data-svc-rs /api/v1/data endpoint. Do NOT unify this with
  // `sanitizeBinForRequest` in src/api/data.ts — the data-svc sanitizer
  // coerces `0` → `null` for LessThan/GreaterThan per REQ-INFRA-002
  // (see eva-data-svc-rs/docs/group-mode/data-endpoints-requirements.md),
  // and that coercion is unsafe for the map/chart path.
  return {
    ...bin,
    BinSize: bin.BinSize === "" ? 1 : bin.BinSize,
    MinSize: bin.MinSize === "" ? null : bin.MinSize,
    MaxBins: bin.MaxBins === "" ? null : bin.MaxBins,
    GreaterThan: bin.GreaterThan === "" ? null : bin.GreaterThan,
    LessThan: bin.LessThan === "" ? null : bin.LessThan
  };
}

/**
 * Removes entries with non-hex color values from ColorLockedItems before the
 * payload is sent to the backend.
 *
 * The C# map service parses every locked color through `Convert.ToUInt32("FF" + hex, 16)`.
 * CSS named colors such as "transparent" are not valid hex and crash the request, causing
 * all wells/facilities to disappear from the map. Omitting the key is the correct
 * representation of "no override" because the backend has no alpha channel anyway —
 * the group simply falls back to its natural palette color.
 */
export function sanitizeColorLockedItems(
  colorLockedItems: Record<string, ColorLockedLegendItem>
): Record<string, ColorLockedLegendItem> {
  return Object.fromEntries(
    Object.entries(colorLockedItems).filter(([, item]) => item.color?.startsWith("#"))
  );
}

/**
 * Normalizes bin values by converting empty strings to appropriate defaults
 * - Empty BinSize becomes 1 (default bin size)
 * - Other empty string values become null
 *
 * When mode.bin is missing, it is left as undefined rather than falling back
 * to the inner groupBy bin. The downstream C# BuildGroupByRequest handler
 * already handles a null mode.Bin by not setting the protobuf field.
 *
 * TODO: reuse this function in chart as well if possible
 */
export function nullBinValues(groupBy: IGroupBy): IGroupBy {
  // A bin saved with HideOthers: true must not reach map-svc, which still honours it.
  const bin = { ...cleanBin(groupBy.bin), HideOthers: false };

  let mode = groupBy.mode;

  if (mode) {
    if (!mode.bin) {
      const key = mode.property ?? "unknown";
      if (!warnedMissingBins.has(key)) {
        if (warnedMissingBins.size >= MAX_WARNED_BINS) {
          warnedMissingBins.clear();
        }
        warnedMissingBins.add(key);
        // eslint-disable-next-line no-console
        console.warn(
          `nullBinValues: mode.bin is missing for mode property "${key}". ` +
            "Leaving mode.bin as undefined; the server will use its default binning."
        );
      }
    }

    mode = {
      ...mode,
      aggregation: mode.aggregation ?? AggregationType.Default,
      bin: mode.bin ? cleanBin(mode.bin) : undefined
    };
  }

  return { ...groupBy, bin, mode };
}
